<template>
  <!-- Wait for permissions before rendering to avoid a 403 flash -->
  <div v-if="!permsLoaded"></div>

  <PageError
    v-else-if="!canViewAlertContact"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />

  <PageLoader v-else-if="loading" />

  <!-- Error State -->
  <PageError v-else-if="error" :error="error" :errorCode="errorCode" />

  <div v-else-if="item" class="p-6 dark:text-white">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBack"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
          </svg>
          Back to Alert Contacts
        </button>
        Alert Contact: {{ item.name }}
      </h1>
    </div>

    <!-- Read-only field table -->
    <div class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60">
      <table class="w-full text-left">
        <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 backdrop-blur-md border-b border-slate-700/50">
          <tr>
            <th class="py-2 px-4 w-1/3">Field</th>
            <th class="py-2 px-4 w-2/3">Value</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="field in displayFields"
            :key="field.key"
            :id="'alert-contact-field-' + field.key"
            class="border-b dark:border-gray-700 hover:bg-gray-900/30 transition-colors"
          >
            <td class="py-4 px-4">{{ field.label }}</td>
            <td class="py-4 px-4">
              <!-- Memberships (read-only pills) -->
              <template v-if="field.key === 'memberships'">
                <div v-if="membershipPills.length" class="flex flex-wrap gap-1">
                  <span
                    v-for="pill in membershipPills"
                    :key="pill.key"
                    class="inline-flex items-center rounded-full bg-blue-600/10 dark:bg-blue-500/20 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300"
                  >
                    {{ pill.label }}
                  </span>
                </div>
                <span v-else class="text-xs font-medium text-amber-600 dark:text-amber-400">No groups</span>
              </template>

              <!-- All other fields (read-only display) -->
              <template v-else>
                {{ formatValue(field.key, item[field.key]) }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { getAlertContactById } from "@/services/alertContactService";
import { useStore } from "@/store";
import PageError from "@/components/PageError.vue";
import PageLoader from "@/components/PageLoader.vue";
import { formatError, errorMessages } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { formatMsisdnForDisplay, loadPhoneNumberUtils } from "@/utils/phoneNumber";

export default {
  name: "AlertContactDetails",
  components: {
    PageError,
    PageLoader,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      item: null,
      loading: true,
      error: "",
      errorCode: "",
      errorMessages,
      displayFields: [
        { key: "name", label: "Name" },
        { key: "msisdn", label: "Mobile Number" },
        { key: "memberships", label: "Alert Groups" },
        { key: "org", label: "Organisation" },
        { key: "created_date", label: "Created Date" },
        { key: "last_synced", label: "Last Synced" },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    canViewAlertContact() {
      return (
        Boolean(this.user?.is_superuser) ||
        useStore.hasPermission("Can view alert contact")
      );
    },
    membershipPills() {
      return (this.item?.groups || []).map((group) => ({
        key: `${group.id}`,
        label: group.label || group.slug,
      }));
    },
  },
  watch: {
    permsLoaded: {
      handler(loaded) {
        // Only fetch once we know the user may view, so a denied user never hits the API.
        if (loaded && this.canViewAlertContact) {
          this.fetchContact();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchContact() {
      const id = this.$route.params.id;
      try {
        this.loading = true;
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to get alert contact details!";
          this.errorCode = "422!";
          return;
        }
        const [item] = await Promise.all([
          getAlertContactById(id, validation.client),
          this.tenant.fetchOrganizations().catch(() => null),
          loadPhoneNumberUtils(),
        ]);
        this.item = item;
      } catch (error) {
        console.error("Error fetching alert contact:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push({
        name: "AlertContacts",
        query: this.tenant.addSchemaParam(),
      });
    },
    formatValue(key, value) {
      if (key === "last_synced") {
        return value ? this.formatDateTime(value) : "Pending sync";
      }
      if (key === "created_date") {
        return value ? this.formatDateTime(value) : "N/A";
      }
      if (key === "msisdn") {
        return value ? formatMsisdnForDisplay(value) : "N/A";
      }
      if (key === "org") {
        const id = this.tenant.currentOrganizationId.value;
        return value || (id ? this.tenant.getOrganizationName(id) : "N/A");
      }
      return value !== null && value !== undefined && value !== "" ? value : "N/A";
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>
